import React, { useState, useEffect } from 'react';
import { dbService } from '../firebaseConfig';
import { Registration, EventSettings } from '../types';

interface RegistrationPageProps {
  onRegistered: (reg: Registration) => void;
}

const inputClass = "w-full px-5 py-4 rounded-2xl bg-brand-sand/60 border border-transparent focus:bg-white focus:border-brand-amber outline-none transition-all";
const labelClass = "text-[10px] font-black text-brand-stone uppercase tracking-widest ml-1";

const RegistrationPage: React.FC<RegistrationPageProps> = ({ onRegistered }) => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<EventSettings | null>(null);
  const [cpfError, setCpfError] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    cpf: '',
    church: '',
    howHeard: '',
    paymentMethod: 'pix' as 'pix' | 'credit_card'
  });

  useEffect(() => {
    dbService.getSettings().then(sett => {
      setSettings(sett);
      if (sett.pauseAutomaticPayments) {
        setFormData(prev => ({ ...prev, paymentMethod: 'pix' }));
      }
    });
  }, []);

  const isValidCPF = (cpf: string) => {
    const cleanCpf = cpf.replace(/\D/g, "");
    if (cleanCpf.length !== 11 || !!cleanCpf.match(/(\d)\1{10}/)) return false;

    let sum = 0;
    let rest;

    for (let i = 1; i <= 9; i++) sum = sum + parseInt(cleanCpf.substring(i - 1, i)) * (11 - i);
    rest = (sum * 10) % 11;
    if ((rest === 10) || (rest === 11)) rest = 0;
    if (rest !== parseInt(cleanCpf.substring(9, 10))) return false;

    sum = 0;
    for (let i = 1; i <= 10; i++) sum = sum + parseInt(cleanCpf.substring(i - 1, i)) * (12 - i);
    rest = (sum * 10) % 11;
    if ((rest === 10) || (rest === 11)) rest = 0;
    if (rest !== parseInt(cleanCpf.substring(10, 11))) return false;

    return true;
  };

  const maskCPF = (v: string) => {
    v = v.replace(/\D/g, "");
    if (v.length > 11) v = v.substring(0, 11);

    if (v.length <= 11) {
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return v;
  };

  const maskPhone = (v: string) => {
    v = v.replace(/\D/g, "");
    if (v.length > 11) v = v.substring(0, 11);
    if (v.length > 2) v = "(" + v.substring(0, 2) + ") " + v.substring(2);
    if (v.length > 9) v = v.substring(0, 10) + "-" + v.substring(10);
    return v;
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskCPF(e.target.value);
    setFormData({ ...formData, cpf: masked });

    // Valida apenas se o CPF estiver completo
    if (masked.replace(/\D/g, "").length === 11) {
      setCpfError(!isValidCPF(masked));
    } else {
      setCpfError(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    if (!isValidCPF(formData.cpf)) {
      setCpfError(true);
      alert('Por favor, insira um CPF válido para continuar.');
      return;
    }

    setLoading(true);
    try {
      const reg = await dbService.addRegistration({
        ...formData,
        amount: settings.eventPrice
      });
      onRegistered(reg);
    } catch (error) {
      alert('Erro ao processar inscrição.');
    } finally {
      setLoading(false);
    }
  };

  if (!settings) return null;

  if (settings.isRegistrationClosed) {
    return (
      <div className="min-h-screen bg-sand-soft flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-[40px] shadow-2xl p-12 text-center border border-brand-sand">
          <div className="w-24 h-24 bg-red-50 text-red-500 rounded-full flex items-center justify-center text-5xl mx-auto mb-8 shadow-lg shadow-red-100">🔒</div>
          <h2 className="font-display text-3xl font-semibold uppercase text-brand-dark mb-4">Inscrições Encerradas</h2>
          <p className="text-brand-stone italic mb-8 leading-relaxed">As inscrições para este congresso já foram encerradas. Fique atento às próximas edições!</p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full py-5 bg-brand-dark text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all"
          >
            Voltar para o Início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-soft py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-ember mb-3">{settings.eventName} • {settings.eventEdition}</p>
          <h2 className="font-display text-5xl font-semibold uppercase text-brand-dark mb-2">Sua Inscrição</h2>
          <p className="text-brand-stone italic">Complete seus dados para garantir sua vaga.</p>
        </div>

        <div className="bg-white rounded-[40px] shadow-2xl overflow-hidden border border-brand-sand">
          <div className="bg-brand-dark p-10 text-white relative overflow-hidden">
            <div className="absolute inset-0 texture-lines"></div>
            <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-brand-amber mb-2">Valor da Inscrição</p>
                <h3 className="text-5xl font-display font-semibold">R$ {settings.eventPrice},00</h3>
              </div>
              <p className="text-white/50 text-xs md:text-right leading-relaxed">{settings.eventDate} de {settings.eventYear}<br />{settings.retreatName}</p>
            </div>
            {settings.paymentInfo && (
              <p className="relative mt-5 pt-4 border-t border-white/10 text-brand-amber text-xs font-bold leading-relaxed">💳 {settings.paymentInfo}</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-10 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className={labelClass}>Nome Completo</label>
                <input required type="text" className={inputClass} value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} />
              </div>
              <div className="space-y-2">
                <label className={`text-[10px] font-black uppercase tracking-widest ml-1 ${cpfError ? 'text-red-500' : 'text-brand-stone'}`}>
                  {cpfError ? 'CPF Inválido' : 'CPF (Obrigatório p/ Seguro)'}
                </label>
                <input
                  required
                  type="text"
                  placeholder="000.000.000-00"
                  className={`w-full px-5 py-4 rounded-2xl border outline-none transition-all ${cpfError ? 'border-red-500 bg-red-50' : 'bg-brand-sand/60 border-transparent focus:bg-white focus:border-brand-amber'}`}
                  value={formData.cpf}
                  onChange={handleCPFChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className={labelClass}>E-mail</label>
                <input required type="email" className={inputClass} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              </div>
              <div className="space-y-2">
                <label className={labelClass}>WhatsApp</label>
                <input required type="tel" className={inputClass} value={formData.phone} onChange={e => setFormData({...formData, phone: maskPhone(e.target.value)})} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className={labelClass}>Igreja</label>
                <input required type="text" className={inputClass} value={formData.church} onChange={e => setFormData({...formData, church: e.target.value})} />
              </div>
              <div className="space-y-2">
                <label className={labelClass}>Como soube?</label>
                <select required className={inputClass} value={formData.howHeard} onChange={e => setFormData({...formData, howHeard: e.target.value})}>
                  <option value="">Selecione...</option>
                  <option value="Redes Sociais">Redes Sociais</option>
                  <option value="Amigo">Amigo</option>
                  <option value="Igreja">Igreja</option>
                </select>
              </div>
            </div>

            {!settings.pauseAutomaticPayments ? (
              <div className="space-y-4 pt-4">
                <label className={labelClass}>Método de Pagamento</label>
                <div className="grid grid-cols-2 gap-4">
                  <button type="button" onClick={() => setFormData({...formData, paymentMethod: 'pix'})} className={`p-6 rounded-3xl border-2 flex flex-col items-center gap-2 transition-all ${formData.paymentMethod === 'pix' ? 'border-brand-amber bg-brand-amber/10' : 'border-gray-100 opacity-60'}`}>
                    <span className="text-2xl">🌀</span>
                    <span className="text-xs font-black uppercase">Pix</span>
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, paymentMethod: 'credit_card'})} className={`p-6 rounded-3xl border-2 flex flex-col items-center gap-2 transition-all ${formData.paymentMethod === 'credit_card' ? 'border-brand-amber bg-brand-amber/10' : 'border-gray-100 opacity-60'}`}>
                    <span className="text-2xl">💳</span>
                    <span className="text-xs font-black uppercase">Cartão</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 bg-brand-sand/60 rounded-3xl border border-brand-sand flex items-center gap-4">
                <div className="text-2xl">🌀</div>
                <div>
                  <p className="text-xs font-black text-brand-dark uppercase">Pagamento via PIX</p>
                  <p className="text-[10px] text-brand-stone">Cartão de crédito temporariamente indisponível.</p>
                </div>
              </div>
            )}

            <button disabled={loading || cpfError} type="submit" className="w-full bg-brand-amber text-brand-night py-5 rounded-[24px] font-black uppercase tracking-widest text-sm hover:bg-brand-dark hover:text-white transition-all disabled:opacity-50 shadow-xl shadow-brand-amber/20">
              {loading ? 'Processando...' : 'Ir para Pagamento'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
